import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Package,
  AlertTriangle,
  Loader2,
  Filter,
  Search,
  Calendar,
  Truck,
  Edit2,
  RotateCcw,
  CheckSquare
} from 'lucide-react';
import { filterItemsBySearch } from '../utils/textSanitize';
import { formatQuantidadeComUnidade } from '../utils/numberFormat';
import DateInputPt from './ui/DateInputPt';

// Converte timestamps do Firebase (objeto _seconds ou string ISO) para Date
const convertFirebaseTimestamps = (obj) => {
  if (!obj) return obj;
  const converted = { ...obj };
  const dateFields = ['data_fabricacao', 'data_validade', 'data_recebimento', 'criado_em', 'atualizado_em'];
  dateFields.forEach((field) => {
    if (converted[field]) {
      if (converted[field]._seconds != null) {
        converted[field] = new Date(converted[field]._seconds * 1000);
      } else if (typeof converted[field] === 'string') {
        converted[field] = new Date(converted[field]);
      }
    }
  });
  return converted;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('pt-BR');
};

const formatarNumeroParaCalculo = (valor) => {
  if (!valor && valor !== 0) return '';
  return String(valor).replace(/\./g, '').replace(',', '.');
};

/** Máximo permitido: 999.999,99 (6 dígitos inteiros + 2 decimais). */
const QUANTIDADE_MAXIMA = 999999.99;

/** Quantidade BR: milhar com "." e até 2 casas com "," enquanto digita. */
const formatarQuantidadeDigitando = (valor) => {
  if (valor == null || valor === '') return '';
  const s = String(valor).replace(/[^\d,]/g, '');
  const partes = s.split(',');
  let intPart = (partes[0] || '').replace(/^0+(?=\d)/, '').slice(0, 6);
  const temVirgula = partes.length > 1;
  const decPart = temVirgula ? partes.slice(1).join('').slice(0, 2) : '';
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (temVirgula) return `${intPart},${decPart}`;
  return intPart;
};

/** Ao sair do campo, fixa exatamente 2 casas decimais (pt-BR) e limita ao máximo. */
const formatarQuantidadeBlur = (valor) => {
  if (valor === '' || valor == null) return '';
  let n = parseFloat(formatarNumeroParaCalculo(valor));
  if (!Number.isFinite(n)) return '';
  if (n > QUANTIDADE_MAXIMA) n = QUANTIDADE_MAXIMA;
  if (n < 0) n = 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const EstoquePage = () => {
  const { hasPermission, companyId, getEmpresaId, isRole } = useAuth();
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [novaQuantidade, setNovaQuantidade] = useState('');
  const [salvandoId, setSalvandoId] = useState(null);

  // Seleção múltipla / zerar em lote
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [zerandoLote, setZerandoLote] = useState(false);
  const [progresso, setProgresso] = useState({ feito: 0, total: 0 });

  // Filtros
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroFornecedor, setFiltroFornecedor] = useState('');
  const [filtroVencimento, setFiltroVencimento] = useState('todos'); // todos | vencidos | a_vencer | range
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  // Filtro por data de recebimento (criado_em)
  const [filtroRecebInicio, setFiltroRecebInicio] = useState('');
  const [filtroRecebFim, setFiltroRecebFim] = useState('');
  // Ordenação (classificação) pelas colunas da tabela — padrão: data de recebimento decrescente
  const [ordenarPor, setOrdenarPor] = useState('recebido'); // nome | fornecedor | lote | recebido | quantidade | vencimento
  const [ordenarDir, setOrdenarDir] = useState('desc'); // asc | desc

  useEffect(() => {
    loadEstoque();
  }, [companyId]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const getEmpresaIdParaRequisicao = () => {
    if (isRole('superadmin')) return undefined;
    return companyId || getEmpresaId?.();
  };

  const handleSalvarQuantidade = async (item) => {
    const qty = parseFloat(formatarNumeroParaCalculo(novaQuantidade));
    if (!Number.isFinite(qty) || qty < 0) {
      setError('Informe uma quantidade válida (número maior ou igual a zero).');
      return;
    }
    if (qty > QUANTIDADE_MAXIMA) {
      setError('A quantidade máxima permitida é 999.999,99.');
      return;
    }
    try {
      setSalvandoId(item.id);
      setError('');
      setSuccess('');
      const updateReceivingQuantity = httpsCallable(functions, 'updateReceivingQuantity');
      await updateReceivingQuantity({
        receivingId: item.id,
        nova_quantidade: qty,
        empresa_id: item.companyId || getEmpresaIdParaRequisicao()
      });
      setEditandoId(null);
      setNovaQuantidade('');
      setSuccess('Quantidade atualizada com sucesso.');
      await loadEstoque();
    } catch (err) {
      console.error('Erro ao atualizar quantidade:', err);
      setError(err.message || 'Erro ao atualizar quantidade.');
    } finally {
      setSalvandoId(null);
    }
  };

  const handleZerarEstoque = async (item) => {
    if (!window.confirm(`Zerar o estoque de "${item.produto_nome || 'N/A'}" (lote: ${Array.isArray(item.lote_fornecedor) ? item.lote_fornecedor.join(', ') : item.lote_fornecedor || '—'})?`)) return;
    try {
      setSalvandoId(item.id);
      setError('');
      setSuccess('');
      const updateReceivingQuantity = httpsCallable(functions, 'updateReceivingQuantity');
      await updateReceivingQuantity({
        receivingId: item.id,
        nova_quantidade: 0,
        empresa_id: item.companyId || getEmpresaIdParaRequisicao()
      });
      setEditandoId(null);
      setSuccess('Estoque zerado com sucesso.');
      await loadEstoque();
    } catch (err) {
      console.error('Erro ao zerar estoque:', err);
      setError(err.message || 'Erro ao zerar estoque.');
    } finally {
      setSalvandoId(null);
    }
  };

  const toggleSelecionado = (id) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleZerarSelecionados = async (alvos) => {
    if (!alvos || alvos.length === 0) return;
    if (
      !window.confirm(
        `Zerar o estoque de ${alvos.length} lote(s) selecionado(s) de uma vez? Esta ação não pode ser desfeita.`
      )
    )
      return;

    setZerandoLote(true);
    setError('');
    setSuccess('');
    setProgresso({ feito: 0, total: alvos.length });

    const updateReceivingQuantity = httpsCallable(functions, 'updateReceivingQuantity');
    let ok = 0;
    let falhas = 0;
    const chunkSize = 5;

    for (let i = 0; i < alvos.length; i += chunkSize) {
      const chunk = alvos.slice(i, i + chunkSize);
      const resultados = await Promise.allSettled(
        chunk.map((item) =>
          updateReceivingQuantity({
            receivingId: item.id,
            nova_quantidade: 0,
            empresa_id: item.companyId || getEmpresaIdParaRequisicao()
          })
        )
      );
      const okChunk = resultados.filter((r) => r.status === 'fulfilled').length;
      ok += okChunk;
      falhas += resultados.length - okChunk;
      setProgresso({ feito: Math.min(i + chunkSize, alvos.length), total: alvos.length });
    }

    setZerandoLote(false);
    setProgresso({ feito: 0, total: 0 });
    setSelecionados(new Set());
    if (falhas > 0) {
      setError(`${falhas} lote(s) não puderam ser zerados. ${ok} zerado(s) com sucesso.`);
    } else {
      setSuccess(`${ok} lote(s) zerado(s) com sucesso.`);
    }
    await loadEstoque();
  };

  const loadEstoque = async () => {
    try {
      setLoading(true);
      setError('');
      const listEstoqueFn = httpsCallable(functions, 'listEstoque');
      const empresaId = isRole('superadmin') ? undefined : (companyId || getEmpresaId?.());
      const result = await listEstoqueFn({
        empresa_id: empresaId,
        sincronizar: false
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao carregar estoque');
      }

      const lotes = (result.data.data?.lotes || []).map(convertFirebaseTimestamps);
      setItens(lotes);
    } catch (err) {
      console.error('Erro ao carregar estoque:', err);
      setError(err.message || 'Erro ao conectar com o servidor. Verifique se há estoque cadastrado.');
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  // Itens com estoque disponível (lotes zerados não aparecem na página)
  const itensAtivos = useMemo(
    () => itens.filter((item) => (Number(item.quantidade_recebida) || 0) > 0),
    [itens]
  );

  // Lista única de fornecedores para o filtro
  const fornecedoresUnicos = useMemo(() => {
    const set = new Set();
    itensAtivos.forEach((item) => {
      if (item.fornecedor) set.add(item.fornecedor);
    });
    return Array.from(set).sort();
  }, [itensAtivos]);

  // Filtrar e ordenar: vencimento crescente (primeiro os que vencem antes)
  const itensFiltrados = useMemo(() => {
    let lista = [...itensAtivos];

    if (filtroNome.trim()) {
      lista = filterItemsBySearch(
        lista,
        filtroNome,
        [
          (item) => item.produto_nome || item.materia_prima_nome || item.nome || '',
          (item) => item.produto_codigo || item.codigo || item.codigo_produto || '',
          (item) =>
            Array.isArray(item.lote_fornecedor)
              ? item.lote_fornecedor.join(' ')
              : item.lote_fornecedor || '',
        ],
        { matchDigits: true }
      );
    }

    if (filtroFornecedor) {
      lista = lista.filter((item) => item.fornecedor === filtroFornecedor);
    }

    // Filtro por data de recebimento (data_recebimento || criado_em)
    if (filtroRecebInicio || filtroRecebFim) {
      const ini = filtroRecebInicio ? new Date(`${filtroRecebInicio}T00:00:00`) : null;
      const fim = filtroRecebFim ? new Date(`${filtroRecebFim}T23:59:59`) : null;
      lista = lista.filter((item) => {
        const rec = item.data_recebimento ?? item.criado_em;
        if (!rec) return false;
        const d = rec instanceof Date ? rec : new Date(rec);
        if (isNaN(d.getTime())) return false;
        if (ini && d < ini) return false;
        if (fim && d > fim) return false;
        return true;
      });
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (filtroVencimento === 'vencidos') {
      lista = lista.filter((item) => {
        const val = item.data_validade;
        if (!val) return false;
        const d = val instanceof Date ? val : new Date(val);
        return !isNaN(d.getTime()) && d < hoje;
      });
    } else if (filtroVencimento === 'a_vencer') {
      const em30Dias = new Date(hoje);
      em30Dias.setDate(em30Dias.getDate() + 30);
      lista = lista.filter((item) => {
        const val = item.data_validade;
        if (!val) return false;
        const d = val instanceof Date ? val : new Date(val);
        return !isNaN(d.getTime()) && d >= hoje && d <= em30Dias;
      });
    } else if (filtroVencimento === 'range' && (filtroDataInicio || filtroDataFim)) {
      const inicio = filtroDataInicio ? new Date(filtroDataInicio) : null;
      const fim = filtroDataFim ? new Date(filtroDataFim) : null;
      lista = lista.filter((item) => {
        const val = item.data_validade;
        if (!val) return false;
        const d = val instanceof Date ? val : new Date(val);
        d.setHours(0, 0, 0, 0);
        if (inicio && d < inicio) return false;
        if (fim && d > fim) return false;
        return true;
      });
    }

    const toDateMs = (val) => {
      if (!val) return null;
      const d = val instanceof Date ? val : new Date(val);
      return isNaN(d.getTime()) ? null : d.getTime();
    };
    const loteStr = (item) =>
      Array.isArray(item.lote_fornecedor)
        ? item.lote_fornecedor.join(', ')
        : String(item.lote_fornecedor || '');

    const valorOrdenacao = (item) => {
      switch (ordenarPor) {
        case 'nome':
          return String(item.produto_nome || item.materia_prima_nome || '').toLocaleLowerCase('pt-BR');
        case 'fornecedor':
          return String(item.fornecedor || '').toLocaleLowerCase('pt-BR');
        case 'lote':
          return loteStr(item).toLocaleLowerCase('pt-BR');
        case 'recebido':
          return toDateMs(item.data_recebimento ?? item.criado_em);
        case 'quantidade':
          return Number(item.quantidade_recebida) || 0;
        case 'vencimento':
        default:
          return toDateMs(item.data_validade);
      }
    };

    const dir = ordenarDir === 'desc' ? -1 : 1;
    lista.sort((a, b) => {
      const va = valorOrdenacao(a);
      const vb = valorOrdenacao(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'pt-BR') * dir;
    });

    return lista;
  }, [
    itensAtivos,
    filtroNome,
    filtroFornecedor,
    filtroVencimento,
    filtroDataInicio,
    filtroDataFim,
    filtroRecebInicio,
    filtroRecebFim,
    ordenarPor,
    ordenarDir
  ]);

  const totalVencidos = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return itensAtivos.filter((item) => {
      const val = item.data_validade;
      if (!val) return false;
      const d = val instanceof Date ? val : new Date(val);
      return !isNaN(d.getTime()) && d < hoje;
    }).length;
  }, [itensAtivos]);

  const totalAvencer30 = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const em30 = new Date(hoje);
    em30.setDate(em30.getDate() + 30);
    return itensAtivos.filter((item) => {
      const val = item.data_validade;
      if (!val) return false;
      const d = val instanceof Date ? val : new Date(val);
      return !isNaN(d.getTime()) && d >= hoje && d <= em30;
    }).length;
  }, [itensAtivos]);

  const podeGerenciar = hasPermission('manage_stock');
  const alvosSelecionados = useMemo(
    () => itensFiltrados.filter((i) => selecionados.has(i.id)),
    [itensFiltrados, selecionados]
  );
  const qtdSelecionados = alvosSelecionados.length;
  const todosSelecionados = itensFiltrados.length > 0 && qtdSelecionados === itensFiltrados.length;
  const algunsSelecionados = qtdSelecionados > 0 && !todosSelecionados;

  const toggleSelecionarTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosSelecionados) {
        itensFiltrados.forEach((i) => next.delete(i.id));
      } else {
        itensFiltrados.forEach((i) => next.add(i.id));
      }
      return next;
    });
  };

  if (!hasPermission('view_stock')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso Negado</h3>
          <p className="mt-1 text-sm text-gray-500">Você não tem permissão para visualizar o estoque.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Estoque</h1>
          <p className="text-gray-600">Estoque de matérias primas, insumos e ingredientes.</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              setLoading(true);
              setError('');
              const listEstoqueFn = httpsCallable(functions, 'listEstoque');
              const empresaId = isRole('superadmin') ? undefined : (companyId || getEmpresaId?.());
              const result = await listEstoqueFn({ empresa_id: empresaId, sincronizar: true });
              if (!result.data?.success) {
                throw new Error(result.data?.message || 'Erro ao sincronizar estoque');
              }
              const lotes = (result.data.data?.lotes || []).map(convertFirebaseTimestamps);
              setItens(lotes);
              setSuccess('Estoque sincronizado com recebimentos aceitos.');
            } catch (err) {
              console.error(err);
              setError(err.message || 'Erro ao sincronizar estoque');
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          Sincronizar recebimentos
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center gap-2">
          {success}
        </div>
      )}

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total de lotes</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{itensAtivos.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Após filtros</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{itensFiltrados.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Vencidos</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{totalVencidos}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">A vencer (30 dias)</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">{totalAvencer30}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nome / Código</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                placeholder="Buscar matéria-prima..."
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fornecedor</label>
            <select
              value={filtroFornecedor}
              onChange={(e) => setFiltroFornecedor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Todos</option>
              {fornecedoresUnicos.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vencimento</label>
            <select
              value={filtroVencimento}
              onChange={(e) => setFiltroVencimento(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="todos">Todos</option>
              <option value="vencidos">Já vencidos</option>
              <option value="a_vencer">A vencer em 30 dias</option>
              <option value="range">Entre datas</option>
            </select>
          </div>
          {filtroVencimento === 'range' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Venc. de</label>
                <DateInputPt
                  value={filtroDataInicio}
                  onChange={setFiltroDataInicio}
                  max={filtroDataFim || undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Venc. até</label>
                <DateInputPt
                  value={filtroDataFim}
                  onChange={setFiltroDataFim}
                  min={filtroDataInicio || undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Recebido de</label>
            <DateInputPt
              value={filtroRecebInicio}
              onChange={setFiltroRecebInicio}
              max={filtroRecebFim || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Recebido até</label>
            <DateInputPt
              value={filtroRecebFim}
              onChange={setFiltroRecebFim}
              min={filtroRecebInicio || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          {(filtroRecebInicio || filtroRecebFim) && (
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  setFiltroRecebInicio('');
                  setFiltroRecebFim('');
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Limpar recebimento
              </button>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Classificar por</label>
            <select
              value={ordenarPor}
              onChange={(e) => setOrdenarPor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="nome">Nome (Ingrediente)</option>
              <option value="fornecedor">Fornecedor</option>
              <option value="lote">Lote</option>
              <option value="recebido">Recebido em</option>
              <option value="quantidade">Quantidade</option>
              <option value="vencimento">Vencimento</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Ordem</label>
            <select
              value={ordenarDir}
              onChange={(e) => setOrdenarDir(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="asc">Crescente (A→Z / mais antigo)</option>
              <option value="desc">Decrescente (Z→A / mais recente)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Barra de ações em lote */}
      {podeGerenciar && (
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <CheckSquare className="h-5 w-5 text-gray-500" />
            {qtdSelecionados > 0 ? (
              <span>
                <strong>{qtdSelecionados}</strong> lote(s) selecionado(s)
                {itensFiltrados.length ? ` de ${itensFiltrados.length}` : ''}
              </span>
            ) : (
              <span className="text-gray-500">
                Selecione lotes para zerar em lote (use a caixa do cabeçalho para marcar todos).
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {zerandoLote && (
              <span className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Zerando {progresso.feito}/{progresso.total}…
              </span>
            )}
            {qtdSelecionados > 0 && !zerandoLote && (
              <button
                type="button"
                onClick={() => setSelecionados(new Set())}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Limpar seleção
              </button>
            )}
            <button
              type="button"
              onClick={() => handleZerarSelecionados(alvosSelecionados)}
              disabled={qtdSelecionados === 0 || zerandoLote}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="h-4 w-4" />
              Zerar selecionados
            </button>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Carregando estoque...</span>
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="text-center py-16">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum item em estoque</h3>
            <p className="mt-1 text-sm text-gray-500">
              {itensAtivos.length === 0
                ? 'Não há lotes com estoque disponível. Lotes zerados não são exibidos.'
                : 'Nenhum lote atende aos filtros aplicados.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {podeGerenciar && (
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                        checked={todosSelecionados}
                        ref={(el) => {
                          if (el) el.indeterminate = algunsSelecionados;
                        }}
                        onChange={toggleSelecionarTodos}
                        disabled={zerandoLote || itensFiltrados.length === 0}
                        title="Selecionar todos (filtrados)"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome (Ingrediente)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[220px]">
                    Fornecedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lote
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recebido em
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantidade
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vencimento
                  </th>
                  {hasPermission('manage_stock') && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {itensFiltrados.map((item) => {
                  const val = item.data_validade;
                  const dataVal = val ? (val instanceof Date ? val : new Date(val)) : null;
                  const isVencido = dataVal && !isNaN(dataVal.getTime()) && dataVal < new Date();
                  const selecionado = selecionados.has(item.id);
                  return (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50 ${selecionado ? 'bg-amber-50' : isVencido ? 'bg-red-50' : ''}`}
                    >
                      {podeGerenciar && (
                        <td className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                            checked={selecionado}
                            onChange={() => toggleSelecionado(item.id)}
                            disabled={zerandoLote || editandoId === item.id}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Package className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.produto_nome || 'N/A'}</div>
                            {item.produto_codigo && (
                              <div className="text-xs text-gray-500">{item.produto_codigo}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 min-w-[220px]">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <Truck className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          {item.fornecedor || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {Array.isArray(item.lote_fornecedor)
                          ? item.lote_fornecedor.join(', ')
                          : item.lote_fornecedor || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(item.data_recebimento ?? item.criado_em)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-sm font-medium text-gray-900">
                        {editandoId === item.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={novaQuantidade}
                              onChange={(e) => setNovaQuantidade(formatarQuantidadeDigitando(e.target.value))}
                              onBlur={() => setNovaQuantidade((prev) => formatarQuantidadeBlur(prev))}
                              className="w-32 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                              placeholder="0,00"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSalvarQuantidade(item);
                                if (e.key === 'Escape') setEditandoId(null);
                              }}
                            />
                            <span className="text-gray-500 text-xs">{item.unidade_medida || ''}</span>
                            <button
                              type="button"
                              onClick={() => handleSalvarQuantidade(item)}
                              disabled={salvandoId === item.id}
                              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {salvandoId === item.id ? '...' : 'Salvar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setEditandoId(null); setNovaQuantidade(''); }}
                              className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <>
                            {formatQuantidadeComUnidade(item.quantidade_recebida, item.unidade_medida)}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 text-sm ${isVencido ? 'text-red-700 font-medium' : 'text-gray-700'}`}>
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(item.data_validade)}
                          {isVencido && <span className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded">Vencido</span>}
                        </span>
                      </td>
                      {hasPermission('manage_stock') && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {editandoId !== item.id && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditandoId(item.id);
                                    const qtd = Number(item.quantidade_recebida ?? 0);
                                    setNovaQuantidade(
                                      Number.isFinite(qtd)
                                        ? qtd.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : ''
                                    );
                                  }}
                                  disabled={salvandoId != null || zerandoLote}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Editar quantidade"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleZerarEstoque(item)}
                                  disabled={salvandoId != null || zerandoLote}
                                  className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                                  title="Zerar estoque"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstoquePage;
